import React from 'react';
import { Box, MenuItem, Select, SelectChangeEvent, Stack, Typography } from '@mui/material';
import { Language } from '../../../models/session';
import { LANGUAGE_OPTIONS, findLanguageOption } from '../languageOptions';

export interface LanguagePickerProps {
    /** The session's current language. Drives both the selected value and the visible flag. */
    currentLanguage: Language;
    onChange: (next: Language) => Promise<void> | void;
    /** Disabled while a persist is in flight to stop the worker double-picking. */
    disabled?: boolean;
}

/**
 * The compact picker that sits in the welcome card top-bar. Worker-facing, so
 * the option label shows the native name first and the English name in parens
 * — operators looking over the worker's shoulder still recognise it.
 *
 * Persistence is the parent's job; this just emits onChange. The S7 fix in
 * `useSessionFormPersistence.shouldRefreshAfterSave` is what re-fetches the
 * session summary and flips every `t()` lookup to the new dictionary.
 */
export const LanguagePicker: React.FC<LanguagePickerProps> = ({
    currentLanguage,
    onChange,
    disabled,
}) => {
    const selected = findLanguageOption(currentLanguage);

    const handleChange = async (event: SelectChangeEvent<Language>) => {
        const next = event.target.value as Language;
        if (next === currentLanguage) return;
        await onChange(next);
    };

    return (
        <Select<Language>
            value={selected.code}
            onChange={handleChange}
            disabled={disabled}
            size="small"
            variant="outlined"
            renderValue={(value) => {
                const opt = findLanguageOption(value);
                return (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                            component="span"
                            className={`fi fi-${opt.flag}`}
                            sx={{
                                width: 22,
                                height: 16,
                                borderRadius: 0.5,
                                boxShadow: '0 0 0 1px rgba(0,0,0,0.08)',
                            }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {opt.nativeName}
                        </Typography>
                    </Stack>
                );
            }}
            sx={{
                minWidth: 180,
                bgcolor: 'background.paper',
                '& .MuiSelect-select': { py: 0.75 },
            }}
        >
            {LANGUAGE_OPTIONS.map((opt) => (
                <MenuItem key={opt.code} value={opt.code}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%' }}>
                        <Box
                            component="span"
                            className={`fi fi-${opt.flag}`}
                            sx={{
                                width: 22,
                                height: 16,
                                borderRadius: 0.5,
                                boxShadow: '0 0 0 1px rgba(0,0,0,0.08)',
                                flexShrink: 0,
                            }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {opt.nativeName}
                        </Typography>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 'auto' }}
                        >
                            {opt.englishName}
                        </Typography>
                    </Stack>
                </MenuItem>
            ))}
        </Select>
    );
};
